import { useState, useEffect, useContext, useRef } from 'react';
import { UserContext } from '../../hooks/UserContext.js';
import styled from 'styled-components';
import axios from 'axios';

// Import components
import TopicsFilter from './TopicsFilter.jsx';
import LawmakerSelect from './LawmakerSelect.jsx';
import { BillFilter } from './BillFilter.jsx';
import KeywordSearch from './KeywordSearch.jsx';
import TipSheetTypeFilter from './TipSheetTypeFilter.jsx';
import FilterChoices from './FilterChoices.jsx';
import OffCanvasFilter from './OffCanvasFilter.jsx';
import ModeToggle from './ModeToggle.jsx';
import ChamberFilter from './ChamberFilter.jsx';
import { getUserPreferences as getSavedUserPreferences } from '../../utils/userPreferences';

const DesktopSidebar = styled.aside`
  display: none;

  @media (min-width: 769px) {
    display: block;
    width: 320px;
    flex-shrink: 0;
    background: #f4f7f9;
    border: 1px solid #ccc;
    border-top: none;
    padding: 1.25rem 1.25rem 2rem;
    position: sticky;
    top: 0;
    align-self: flex-start;
    height: 100%;
    min-height: 100vh;
    overflow-y: auto;
    box-sizing: border-box;
  }
`;

const FloatingFilterWrapper = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
    position: fixed;
    right: 1rem;
    bottom: 1rem;
    z-index: 999;
    border-radius: 999px;

    &::before {
      content: '';
      position: absolute;
      top: -0.75rem;
      left: -0.75rem;
      right: -0.75rem;
      bottom: -0.75rem;
      background: rgba(255, 255, 255, 0.8);
      backdrop-filter: blur(2px);
      border-radius: 0.5rem;
      z-index: -1;
    }
  }

  &[data-hidden='true'] {
    display: none;
  }
`;

const FloatingFilterButton = styled.button`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 44px;
    min-width: 150px;
    padding: 0 1rem;
    background: #0a819f;
    color: #ffffff;
    border: 1px solid #0a819f;
    border-radius: 0.5rem;
    font-size: 0.925rem;
    font-weight: 600;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
    cursor: pointer;
    gap: 0.5rem;
    text-transform: none;
  }
`;

const SidebarSection = styled.div`
  padding: 1rem 0;
  border-bottom: 1px solid #ccc;

  &:last-of-type {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const SidebarLabel = styled.div`
  font-size: 0.9rem;
  color: #000000;
  margin-bottom: 1rem;
`;

const SidebarField = styled.div`
  position: relative;
  width: 100%;
`;

export default function Filter({
  tags,
  setTags,
  sortBy,
  setSortBy,
  legislators,
  setLegislators,
  setExclusions,
  tipsheetType,
  setTipsheetType,
  setSearchStories,
  setSearchActive,
  originalStories,
  searchActive,
  setFilteredStoriesByBill,
  setBillFilterActive,
  setSelectedBills,
  selectedBills,
  setKeywords,
  searchInputRef,
  keywords = '',
  clusterBy = 'none',
  setClusterBy,
  setCoverageActive,
  coverageActive,
  setCoverageMessage,
  allTags,
  geoTags,
  lawmakerOptions,
  chamberFilter,
  setChamberFilter,
  tipSheetCount,
}) {
  const { user } = useContext(UserContext);
  const [offcanvasOpen, setOffcanvasOpen] = useState(false);
  const [topicMenuOpen, setTopicMenuOpen] = useState(false);
  const [selectedExclusions, setSelectedExclusions] = useState([]);

  const topicDropdownRef = useRef(null);

  const hideInfo = [
    { key: 'appropriations', text: 'Appropriations Hearings' },
    { key: 'zero', text: '0 Score Tip Sheets' },
  ];

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        topicDropdownRef.current &&
        !topicDropdownRef.current.contains(event.target)
      ) {
        setTopicMenuOpen(false);
      }
    }

    if (topicMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [topicMenuOpen]);

  // Handle topic selection
  const handleTopicClick = (tag) => {
    setTags((prevTags) => {
      if (prevTags.includes(tag)) {
        return prevTags;
      } else {
        return [...prevTags, tag];
      }
    });
  };

  // Handle lawmaker selection
  const handleLawmakerSelect = (selectedOption) => {
    if (!selectedOption) return;

    const lawmakerId = selectedOption.value;
    if (legislators.some((legislator) => legislator.pid === lawmakerId)) return;

    const selectedLawmaker = lawmakerOptions.find(
      (lawmaker) => lawmaker.pid === lawmakerId
    );

    if (selectedLawmaker) {
      const { pid, first, last, party } = selectedLawmaker;
      setLegislators([...legislators, { pid, first, last, party }]);
    }
  };

  // Handle exclusions
  const handleExclusionsClick = (item) => {
    const newExclusions = selectedExclusions.includes(item)
      ? selectedExclusions.filter((exclusion) => exclusion !== item)
      : [...selectedExclusions, item];

    setSelectedExclusions(newExclusions);
    setExclusions(newExclusions);
  };

  // Search functionality
  const performSearch = async (searchTerm) => {
    if (!searchTerm?.trim()) {
      setSearchActive(false);
      setSearchStories([]);
      return;
    }

    try {
      const axiosT = axios.create({
        baseURL: `${process.env.REACT_APP_FEATURED_API}`,
        headers: {
          Authorization: `JWT ${localStorage.getItem('token')}`,
        },
      });

      const response = await axiosT.get(
        `search/?p=main&q=${encodeURIComponent(searchTerm).replaceAll(
          '%2B',
          '+'
        )}`
      );

      const searchResults = Object.entries(response.data.tipsheets)
        .map(([id, data]) => ({
          id,
          relevance: data.relevance,
        }))
        .sort((a, b) => b.relevance - a.relevance)
        .map(({ id }) => id);

      const matchedStories = searchResults
        .map((id) => originalStories.find((story) => story.id === id))
        .filter(Boolean);

      setSearchActive(true);
      setSearchStories(matchedStories);
    } catch (err) {
      console.log('Error fetching search data:', err);
    }
  };

  // Get user preferences
  const getUserPreferences = () => {
    if (!user) return;

    const {
      tags: userTags,
      keywordString,
      legislators: userLegislators,
      clusterBy: userClusterBy,
      hasPreferences,
    } = getSavedUserPreferences(user);

    if (userTags.length) {
      setTags(userTags);
    }

    if (keywordString) {
      setKeywords(keywordString);
      if (keywordString && originalStories.length > 0) {
        performSearch(keywordString);
      }
    }

    if (userLegislators.length) {
      setLegislators(userLegislators);
    }

    if (userClusterBy) {
      setClusterBy(userClusterBy);
    }

    if (hasPreferences) {
      setCoverageMessage(false);
    }
  };

  // Handle mode toggle
  const handleModeToggle = (isExploreMode) => {
    // helper to fully clear search/filter state
    const clearSearchState = () => {
      setLegislators([]);
      setTags([]);
      setSelectedBills([]);
      setKeywords('');
      setSearchStories([]);
      setSearchActive(false);
      // clear the visible input if a ref was provided and notify input listeners
      if (searchInputRef && searchInputRef.current) {
        try {
          searchInputRef.current.value = '';
          // dispatch an input event so children that listen react to the change
          const ev = new Event('input', { bubbles: true });
          searchInputRef.current.dispatchEvent(ev);
        } catch (e) {
          // ignore
        }
      }
      // ensure performSearch clears any server-side search state / results
      if (typeof performSearch === 'function') {
        performSearch('');
      }
    };

    if (isExploreMode && coverageActive) {
      // switching to All Tip Sheets: fully clear previous filters / searches
      clearSearchState();
    } else if (!isExploreMode) {
      // switching to My Filters: clear transient UI/search state first,
      // then load saved user preferences (which may set keywords)
      clearSearchState();
      getUserPreferences();
    }
    setCoverageActive(!isExploreMode);
  };

  // Create shared state for filter components
  const filterState = {
    tags,
    setTags,
    allTags,
    geoTags,
    tipsheetType,
    selectedExclusions,
    sortBy,
    clusterBy,
    searchActive,
    keywords,
    legislators,
    lawmakerOptions,
    chamberFilter,
  };

  // Create shared handlers for filter components
  const filterHandlers = {
    handleTopicClick,
    handleLawmakerSelect,
    handleExclusionsClick,
    setTipsheetType,
    setSortBy,
    setClusterBy,
    performSearch,
    setKeywords,
    setSearchActive,
    setSearchStories,
    setChamberFilter,
    setTags,
    setLegislators,
  };

  return (
    <>
      <DesktopSidebar aria-label="Filter tip sheets">
        <ModeToggle
          myFiltersActive={coverageActive}
          onToggle={handleModeToggle}
        />

        <FilterChoices
          tags={tags}
          setTags={setTags}
          legislators={legislators}
          setLegislators={setLegislators}
          selectedBills={selectedBills}
          setSelectedBills={setSelectedBills}
          keywords={keywords}
          setKeywords={setKeywords}
          setBillFilterActive={setBillFilterActive}
          setFilteredStoriesByBill={setFilteredStoriesByBill}
          setSearchActive={setSearchActive}
          setSearchStories={setSearchStories}
          searchInputRef={searchInputRef}
          originalStories={originalStories}
          coverageActive={coverageActive}
          chamberFilter={chamberFilter}
          setChamberFilter={setChamberFilter}
          variant="sidebar"
        />

        <SidebarSection>
          <SidebarLabel>Topics</SidebarLabel>
          <SidebarField ref={topicDropdownRef}>
            <TopicsFilter
              isOpen={topicMenuOpen}
              setIsOpen={setTopicMenuOpen}
              allTags={allTags}
              geoTags={geoTags}
              handleTopicClick={handleTopicClick}
              isMobile={false}
              isSidebar
            />
          </SidebarField>
        </SidebarSection>

        <SidebarSection>
          <SidebarLabel>Legislators</SidebarLabel>
          <LawmakerSelect
            lawmakers={lawmakerOptions}
            handleLawmakerSelect={handleLawmakerSelect}
            isMobile={false}
            chamberFilter={chamberFilter}
            setChamberFilter={setChamberFilter}
            showChamber={false}
          />
        </SidebarSection>

        <SidebarSection>
          <SidebarLabel>Bills</SidebarLabel>
          <BillFilter
            originalStories={originalStories}
            setFilteredStoriesByBill={setFilteredStoriesByBill}
            setFilterActive={setBillFilterActive}
            setSelectedBills={setSelectedBills}
            selectedBills={selectedBills}
            isMobile={false}
            isSidebar
          />
        </SidebarSection>

        <SidebarSection>
          <SidebarLabel>Keywords</SidebarLabel>
          <KeywordSearch
            keywords={keywords}
            setKeywords={setKeywords}
            performSearch={performSearch}
            setSearchActive={setSearchActive}
            setSearchStories={setSearchStories}
            searchInputRef={searchInputRef}
            isMobile={false}
            fullWidth
          />
        </SidebarSection>

        <SidebarSection>
          <ChamberFilter
            chamberFilter={chamberFilter}
            setChamberFilter={setChamberFilter}
          />
        </SidebarSection>

        <SidebarSection>
          <TipSheetTypeFilter
            tipsheetType={tipsheetType}
            setTipsheetType={setTipsheetType}
            selectedExclusions={selectedExclusions}
            handleExclusionsClick={handleExclusionsClick}
            hideInfo={hideInfo}
            isMobile={false}
            variant="sidebar"
          />
        </SidebarSection>
      </DesktopSidebar>

      <FloatingFilterWrapper data-hidden={offcanvasOpen ? 'true' : 'false'}>
        <FloatingFilterButton
          type="button"
          aria-label="Open filters"
          onClick={() => setOffcanvasOpen(true)}
        >
          <svg
          width="17"
          height="17"
          viewBox="0 0 17 17"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
          focusable="false"
        >
          <path
            d="M6.625 2.25H0.5M8.375 14.5H0.5M10.125 0.5V4M11.875 12.75V16.25M16.25 8.375H8.375M16.25 14.5H11.875M16.25 2.25H10.125M4.875 6.625V10.125M4.875 8.375H0.5"
            stroke="white"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Show Filters
      </FloatingFilterButton>
      </FloatingFilterWrapper>

      <OffCanvasFilter
        isOpen={offcanvasOpen}
        setIsOpen={setOffcanvasOpen}
        filterState={filterState}
        filterHandlers={filterHandlers}
        originalStories={originalStories}
        setFilteredStoriesByBill={setFilteredStoriesByBill}
        setBillFilterActive={setBillFilterActive}
        selectedBills={selectedBills}
        setSelectedBills={setSelectedBills}
        hideInfo={hideInfo}
        tipSheetCount={tipSheetCount}
      />
    </>
  );
}
